import React from 'react';
import { Modal } from '@alefeducation/ui-elements-library-core/lib/Modal';
import styled from 'styled-components';
import { Button, Heading } from '@alefeducation/ui-elements-library-core';
import TrashSolid from '@alefeducation/ui-elements-library-icons/lib/components/Icons/TrashSolid';
import {
  CheckCircle,
  ExclamationCircle,
  Times,
  TimesCircle,
} from '@alefeducation/ui-elements-library-icons';

export interface MessagePopupPropsType {
  open: boolean;
  title: string;
  name: string;
  message: string;
  buttonText?: string;
  onClose: (e: React.MouseEvent) => void;
  onAction?: (e: React.MouseEvent) => void;
  variant: 'warning' | 'error' | 'success';
}

const RenderIcon = (props: { variant: 'warning' | 'error' | 'success' }) => {
  const { variant } = props;

  switch (variant) {
    case 'warning':
      return (
        <ExclamationCircle
          height={60}
          width={60}
          color="warning.100"
          className="popver-message__icon__warning"
          data-testid="popver-message_icon_warning"
        />
      );
    case 'success':
      return (
        <CheckCircle
          height={60}
          width={60}
          color="success.100"
          className="popver-message__icon__success"
          data-testid="popver-message_icon_success"
        />
      );
    case 'error':
      return (
        <TimesCircle
          height={60}
          width={60}
          color="error.100"
          className="popver-message__icon__error"
          data-testid="popver-message_icon_error"
        />
      );
  }
};

const PopoverMessageModal = styled.div`
  display: flex;
  flex-direction: column;
`;

const PopoverMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PopoverMessageHeading = styled.div`
  text-align: center;
`;

const PopoverMessageBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }: any) => theme.colors.primary[100]};
  span {
    font-size: 14px;
    line-height: 16px;
    font-weight: 300;
    white-space: pre-wrap;
  }
`;

const PopoverMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  border-bottom: 1px solid $breakLine;
  padding: 55px 40px 24px;
`;

const PopoverActions = styled.div`
  display: flex;
  width: 400px;
  justify-content: space-evenly;
  padding: 0px 60px 15px;
  margin: 20px auto;
  button {
    width: 120px;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  cursor: pointer;
`;

const MessagePopup = (props: MessagePopupPropsType) => {
  const {
    title,
    message,
    name,
    buttonText = 'OK',
    onClose,
    onAction = (e) => onClose(e),
    variant,
    open,
  } = props;

  return (
    <PopoverMessageModal>
      <Modal open={open} size="medium">
        <CloseIcon>
          <Times
            className="close-icon"
            color="primary.40"
            height={14}
            width={14}
            onClick={onClose}
            data-testid="popover-message-close"
          />
        </CloseIcon>
        <PopoverMessageContainer className="message-container">
          <div className="popver-message__icon">
            <RenderIcon variant={variant} />
          </div>
          <PopoverMessage className="message">
            <PopoverMessageHeading className="popover-message__container__message__heading">
              <Heading variant="heading-small" weight="bold">
                {title}
              </Heading>
            </PopoverMessageHeading>
            <PopoverMessageBody className="popover-message__container__message__body">
              <span>{message}</span>
            </PopoverMessageBody>
          </PopoverMessage>
        </PopoverMessageContainer>
        <PopoverActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={onClose}
            data-testid="popover-message-close"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={onAction}
            icon={<TrashSolid />}
            data-testid="popover-message-action"
          >
            {buttonText}
          </Button>
        </PopoverActions>
      </Modal>
    </PopoverMessageModal>
  );
};

export default MessagePopup;
